import { Box } from "@mui/system";
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, Tooltip } from "@mui/material";
import { Link, useMatch } from "@tanstack/react-router";
import { BarChart2, Monitor, Smartphone } from "react-feather";
import { useTranslation } from "react-i18next";
import { APP_SIDEBAR_WIDTH, NAVIGATION_HEIGHT } from "@/features/navigation/consts";
import { ciCustomColors } from "@/theme/colors";
import { Paths as ConsentSolutionsPaths } from "@/features/consent-solutions";
import { Paths as ComplianceDashboardPaths } from "@/features/compliance-dashboard";
import RedirectsMap from "@/config/redirects-map";
import { RenewConsentButton } from "../renew-consent-button";

const navigationOptions = [
  {
    labelKey: "navigation.sidebar.consent_solutions_link",
    to: ConsentSolutionsPaths.Root,
    icon: <Monitor />,
  },
  {
    labelKey: "navigation.sidebar.compliance_dashboard_link",
    to: ComplianceDashboardPaths.PowerBiReport,
    icon: <BarChart2 />,
  },
  {
    labelKey: "navigation.sidebar.mobile_consent_solutions_link",
    to: `${RedirectsMap.ServerRootUrl}${RedirectsMap.MobileConsentSolutions}`,
    icon: <Smartphone />,
    openInNewTab: true,
  },
];

export const AppSidebar = () => {
  const { t } = useTranslation("coi-platform-translations");

  const consentSolutions = useMatch({ from: "/_authorized/consent-solutions", shouldThrow: false });
  const complianceDashboard = useMatch({ from: "/_authorized/compliance-dashboard/", shouldThrow: false });

  const selectedRoute = (() => {
    if (consentSolutions) return ConsentSolutionsPaths.Root;
    if (complianceDashboard) return ComplianceDashboardPaths.PowerBiReport;

    return null;
  })();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: APP_SIDEBAR_WIDTH,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: APP_SIDEBAR_WIDTH,
          boxSizing: "border-box",
          paddingTop: NAVIGATION_HEIGHT,
          border: "none",
          backgroundColor: "transparent",
        },
      }}
    >
      <Box
        sx={{
          overflow: "hidden",
          display: "flex",
          padding: "32px 40px 32px 32px",
          height: "100%",
          backgroundColor: "transparent",
        }}
      >
        <List
          sx={{
            minWidth: "60px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0px 2px 10px 0px #0000001A, 0px 1px 1px 0px #0000001F",
            backgroundColor: "#FFFFFF",
            borderRadius: "5px",
            padding: "8px 4px",
            gap: "12px",
          }}
        >
          {navigationOptions.map(({ labelKey, to, icon, openInNewTab }) => {
            const isItemSelected = selectedRoute === to;

            return (
              <Tooltip placement="right" title={t(labelKey)} key={labelKey}>
                <ListItem disablePadding component={Link} to={to} target={openInNewTab ? "_blank" : undefined}>
                  <ListItemButton
                    sx={{
                      borderRadius: "4px",
                      py: "6px",
                      cursor: "pointer",
                      "&.Mui-selected": { backgroundColor: ciCustomColors.blue[450] },
                    }}
                    selected={isItemSelected}
                    disableGutters
                  >
                    <ListItemIcon
                      sx={{
                        color: isItemSelected ? ciCustomColors.blue[500] : "#000000",
                        minWidth: "unset",
                        width: "100%",
                        justifyContent: "center",
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}

          <ListItem disablePadding sx={{ marginTop: "auto" }}>
            <RenewConsentButton />
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};
